import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, Empty, List, Space, Tabs, Tag, Typography } from 'antd'
import { BankOutlined, ShopOutlined } from '@ant-design/icons'
import { IntegrationIcons } from '../integration-icons'
import {
  EmployeeRoles,
  InviteButton,
  StatusDot,
  employeeInitials,
  flattenOrganizations,
  inviteExceptionLabel,
  sortActiveFirst,
} from './shared'
import type { CompanyStructureViewProps, Organization } from './types'

const { Text, Paragraph } = Typography

type NavItem =
  | { type: 'brand'; id: string; title: string }
  | { type: 'org'; id: string; title: string; org: Organization; brandName?: string }

/**
 * V2 — Master-detail.
 * Слева навигация по орг., справа выбранный офис и сотрудники.
 * Приглашение только в контексте выбранного активного офиса.
 */
export function CompanyStructureV2Split({ structure, onInvite }: CompanyStructureViewProps) {
  const orgs = useMemo(() => flattenOrganizations(structure), [structure])
  const brandMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const b of structure.brands) map.set(b.id, b.name)
    return map
  }, [structure.brands])

  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = []
    for (const brand of structure.brands.filter(b => b.organizations.length > 0)) {
      items.push({ type: 'brand', id: `brand-${brand.id}`, title: brand.name })
      for (const org of sortActiveFirst(brand.organizations)) {
        items.push({
          type: 'org',
          id: `org-${org.id}`,
          title: org.name,
          org,
          brandName: brand.name,
        })
      }
    }
    for (const org of sortActiveFirst(structure.organizations_without_brand)) {
      items.push({ type: 'org', id: `org-${org.id}`, title: org.name, org })
    }
    return items
  }, [structure])

  const firstOrgId = orgs[0]?.id
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(firstOrgId ?? null)
  const [selectedOfficeId, setSelectedOfficeId] = useState<number | null>(null)

  const selectedOrg = orgs.find(o => o.id === selectedOrgId) || null
  const offices = useMemo(
    () => (selectedOrg ? sortActiveFirst(selectedOrg.offices) : []),
    [selectedOrg],
  )

  useEffect(() => {
    if (!selectedOrg) {
      setSelectedOfficeId(null)
      return
    }
    const stillThere = offices.some(o => o.id === selectedOfficeId)
    if (!stillThere) {
      setSelectedOfficeId(offices[0]?.id ?? null)
    }
  }, [selectedOrg, offices, selectedOfficeId])

  const selectedOffice = offices.find(o => o.id === selectedOfficeId) || null
  const employees = selectedOffice ? sortActiveFirst(selectedOffice.employees) : []

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(180px, 240px) 1fr',
        gap: 0,
        minHeight: 360,
        overflow: 'hidden',
      }}
    >
      <aside
        style={{
          borderRight: '1px solid #f0f0f0',
          background: '#fafafa',
          padding: '8px 0',
          maxHeight: 520,
          overflow: 'auto',
        }}
      >
        {navItems.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет орг." />
        ) : (
          navItems.map(item => {
            if (item.type === 'brand') {
              return (
                <div
                  key={item.id}
                  style={{
                    padding: '6px 12px',
                    fontSize: 11,
                    color: '#8c8c8c',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  <ShopOutlined style={{ marginRight: 6 }} />
                  {item.title}
                </div>
              )
            }
            const active = item.org.id === selectedOrgId
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedOrgId(item.org.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  border: 'none',
                  background: active ? '#fff' : 'transparent',
                  borderLeft: active ? '3px solid #1677ff' : '3px solid transparent',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  opacity: item.org.is_active ? 1 : 0.55,
                }}
              >
                <BankOutlined style={{ color: active ? '#1677ff' : '#8c8c8c' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: active ? 600 : 400,
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.title}
                  </div>
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                    {item.org.offices.length} офис.
                    {item.org.type_name ? ` · ${item.org.type_name}` : ''}
                  </div>
                </span>
                <StatusDot active={item.org.is_active} />
              </button>
            )
          })
        )}
      </aside>

      <section style={{ padding: 16, minWidth: 0 }}>
        {!selectedOrg ? (
          <Empty description="Выберите организацию" />
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <Space size={8} wrap>
                <Link to={`/new/organization/${selectedOrg.id}`}>
                  <Text strong style={{ fontSize: 16 }}>
                    {selectedOrg.name}
                  </Text>
                </Link>
                {!selectedOrg.is_active && <Tag>Неактивна</Tag>}
                {selectedOrg.type_name && <Tag color="blue">{selectedOrg.type_name}</Tag>}
                {selectedOrg.brand_id && brandMap.get(selectedOrg.brand_id) && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {brandMap.get(selectedOrg.brand_id)}
                  </Text>
                )}
              </Space>
            </div>

            {offices.length === 0 ? (
              <Paragraph type="secondary">
                Нет офисов. <Link to={`/office/${selectedOrg.id}/new`}>Добавить офис</Link>
              </Paragraph>
            ) : (
              <>
                <Tabs
                  size="small"
                  activeKey={selectedOfficeId ? String(selectedOfficeId) : undefined}
                  onChange={key => setSelectedOfficeId(Number(key))}
                  items={offices.map(office => ({
                    key: String(office.id),
                    label: (
                      <Space size={6}>
                        <span style={{ opacity: office.is_active ? 1 : 0.5 }}>{office.name}</span>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {office.employees.length}
                        </Text>
                        {!office.is_active && <StatusDot active={false} title="Офис неактивен" />}
                      </Space>
                    ),
                  }))}
                />

                {selectedOffice && (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Space size={8} wrap>
                        <Link to={`/office/${selectedOffice.id}`}>
                          <Text type="secondary">Карточка офиса</Text>
                        </Link>
                        {selectedOrg.type_name && (
                          <Tag color="blue">{selectedOrg.type_name}</Tag>
                        )}
                        <IntegrationIcons integrations={selectedOffice.integrations} size={16} />
                      </Space>
                      {selectedOffice.is_active && selectedOrg.is_active ? (
                        <InviteButton
                          org={selectedOrg}
                          office={selectedOffice}
                          onInvite={onInvite}
                        />
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Приглашение недоступно для неактивных
                        </Text>
                      )}
                    </div>

                    {employees.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет сотрудников" />
                    ) : (
                      <List
                        size="small"
                        dataSource={employees}
                        renderItem={emp => {
                          const exception = inviteExceptionLabel(emp)
                          return (
                            <List.Item
                              style={{
                                opacity: emp.is_active || exception ? 1 : 0.45,
                                paddingLeft: 0,
                                paddingRight: 0,
                              }}
                            >
                              <List.Item.Meta
                                avatar={
                                  <Avatar size={36} style={{ background: '#1677ff' }}>
                                    {employeeInitials(emp)}
                                  </Avatar>
                                }
                                title={
                                  <Space size={8} wrap>
                                    <Link to={`/employees_new/${emp.id}`}>
                                      {emp.name || emp.login}
                                    </Link>
                                    {exception}
                                    <IntegrationIcons
                                      integrations={emp.integrations}
                                      size={14}
                                      services={['mango']}
                                    />
                                  </Space>
                                }
                                description={
                                  <Space direction="vertical" size={4}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      {emp.login}
                                    </Text>
                                    <EmployeeRoles roles={emp.roles} />
                                  </Space>
                                }
                              />
                            </List.Item>
                          )
                        }}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>
    </div>
  )
}
